/* istanbul ignore file */
import { useEffect } from 'react'

const getCurrentFocusableElems = (popupRef: React.RefObject<HTMLDivElement>) =>
  (popupRef?.current?.querySelectorAll(
    [
      'button:not([disabled])',
      '[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', '),
  ) as NodeListOf<HTMLElement>) ?? []

const focusFirstElement = (popupRef: React.RefObject<HTMLDivElement>) => {
  getCurrentFocusableElems(popupRef)?.[0]?.focus()
}

/**
 * Focus management: focus first focusable element when popup opens and create focus trap.
 * @param popupRef - ref to the popup element
 * @param isPopupOpened - whether the popup is opened
 * @param isMobile - whether the screen is mobile
 */
export const useDropdownFocusTrap = ({
  popupRef,
  isPopupOpened,
  isMobile,
}: {
  popupRef: React.RefObject<HTMLDivElement>
  isPopupOpened: boolean
  isMobile: boolean
}) => {
  useEffect(() => {
    if (isPopupOpened && !isMobile && popupRef.current) {
      // Use a small delay to ensure the content is rendered
      const timer = setTimeout(() => {
        focusFirstElement(popupRef)
      }, 0)

      const handleTabKey = (event: KeyboardEvent) => {
        if (event.key === 'Tab') {
          const focusableElements = getCurrentFocusableElems(popupRef)

          const firstElement = focusableElements[0]
          const lastElement = focusableElements[focusableElements.length - 1]

          if (document.activeElement === lastElement && !event.shiftKey) {
            event.preventDefault()
            firstElement.focus()
          }

          if (document.activeElement === firstElement && event.shiftKey) {
            event.preventDefault()
            lastElement.focus()
          }
        }
      }

      popupRef.current.addEventListener('keydown', handleTabKey)

      return () => {
        clearTimeout(timer)
        popupRef.current?.removeEventListener('keydown', handleTabKey)
      }
    }
  }, [isPopupOpened, isMobile])
}
